import { motion } from "framer-motion";
import { workerUploads } from "@/data/showcase";
import { fadeUp, staggerSlow, viewportOnce } from "@/lib/motion";

const { eyebrow, titleStart, titleItalic, body, claim, filtersLabel, filters, closing } =
  workerUploads;

// Second intake pipeline (worker phone uploads) — parallel to the CCTV
// scenarios. Same dark editorial language: one photo-led "verified claim"
// example, then a typographic list of what AI filters out before it reaches
// the coordinator.
export function WorkerUploads() {
  return (
    <section
      id="uploads"
      className="relative bg-[#06080F] border-t border-ink-900 py-16 md:py-24 overflow-hidden"
    >
      <div className="relative mx-auto max-w-[1080px] px-6">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="mb-12 md:mb-16 max-w-[760px]"
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500 mb-3">
            {eyebrow}
          </p>
          <h2 className="font-serif text-white leading-[1.05] text-3xl md:text-4xl lg:text-5xl max-w-[22ch]">
            {titleStart}{" "}
            <span className="text-foton-lighter italic font-display">{titleItalic}</span>
          </h2>
          <p className="text-base md:text-lg text-ink-300 leading-relaxed max-w-[640px] mt-6">
            {body}
          </p>
        </motion.div>

        {/* Verified-claim example: worker's words left, AI verdict + photo right */}
        <motion.article
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="grid md:grid-cols-[1fr_1fr] gap-8 md:gap-12 items-start mb-16 md:mb-20"
        >
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-500 mb-3">
              {claim.worker}
            </p>
            <blockquote className="font-serif text-2xl md:text-3xl text-white leading-[1.2] mb-6">
              {claim.claimText}
            </blockquote>
            <span className="inline-flex items-center gap-2 rounded-full border border-foton-light/40 bg-foton-light/10 px-3 py-1">
              <span
                aria-hidden
                className="w-1.5 h-1.5 rounded-full bg-foton-light"
                style={{ boxShadow: "0 0 8px #8D51FF" }}
              />
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-foton-lighter">
                {claim.matchLabel}
              </span>
            </span>
          </div>
          <div>
            <img
              src={claim.img}
              alt={claim.imgAlt}
              loading="lazy"
              className="w-full aspect-[4/3] object-cover mb-3"
            />
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-500 mb-2">
              {claim.area} · {claim.phase}
            </p>
            <p className="text-sm text-ink-300 leading-relaxed">{claim.aiVerdict}</p>
          </div>
        </motion.article>

        <motion.p
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-600 mb-6"
        >
          {filtersLabel}
        </motion.p>

        <motion.dl
          variants={staggerSlow}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="grid md:grid-cols-2 gap-x-12 gap-y-px"
        >
          {filters.map((f) => (
            <motion.div
              key={f.tag}
              variants={fadeUp}
              className="grid grid-cols-1 gap-1 py-5 border-b border-ink-800 last:border-b-0"
            >
              <dt>
                {f.emphasis ? (
                  <span className="inline-block rounded bg-foton-light px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white">
                    {f.tag}
                  </span>
                ) : (
                  <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-foton-lighter">
                    {f.tag}
                  </span>
                )}
              </dt>
              <dd className="text-sm text-ink-300 leading-relaxed max-w-[440px]">
                {f.line}
              </dd>
            </motion.div>
          ))}
        </motion.dl>

        <motion.p
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="text-sm md:text-base text-ink-400 leading-relaxed max-w-[640px] mt-12"
        >
          {closing}
        </motion.p>
      </div>
    </section>
  );
}
